"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Menu, Search, ShoppingCart, User, ChevronDown, X, Heart } from "lucide-react";
import { useUser } from "@/providers/userContext";
import { AxiosPost_Nocatch } from "@/utilities/axioscall";
import { AK } from "@/constants";
// import { useCart } from "../themes/ui/cart-provider";
import { Button } from "../themes/ui/button";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/themes/ui/sheet"; // Updated import
// import { SearchOverlay } from "../themes/search-overlay";
import Image from "next/image";
import { useCart } from "../home/cart-provider";
import { SearchOverlay } from "../home/search-overlay";
import { getLogo } from "@/services/storeService";

interface Category {
  name: string;
  seo_url: string;
  meta_tag_title: string;
  meta_tag_desc: string;
  cat_img: null | string;
  banner: null | string;
  subcat: number;
  parent_id: number;
  sortorder: number;
  display_in_menu: number;
  id: number;
}

interface NavbarProps {
  categoryListData: Category[];
}

const Navbar = ({ categoryListData }: NavbarProps) => {
  const { itemCount } = useCart();
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState<string | null>(null);
  const [showSearchOverlay, setShowSearchOverlay] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const { cartinfoData, setCartInfoData, login, logout, isLoggedIn, user } = useUser();


  // Filter main categories (parent_id: 0), sort by sortorder, and take first 5
  const mainCategories = React.useMemo(() => {
    return categoryListData
      .filter((cat) => cat.parent_id === 0 && cat.display_in_menu === 1)
      .sort((a, b) => a.sortorder - b.sortorder)
      .slice(0, 5);
  }, [categoryListData]);

  // Function to get subcategories for a given main category
  const getSubCategories = (parentId: number): Category[] => {
    return categoryListData
      .filter((cat) => cat.parent_id === parentId)
      .sort((a, b) => a.sortorder - b.sortorder);
  };


  const handleMouseEnter = (itemName: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(itemName);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 100);
  };

  const handleLogout = async () => {
    try {
      console.log("Initiating logout...");
      const response = await AxiosPost_Nocatch(AK.LOGOUTAPI, {}, true);
      console.log("Logout API response:", response);
      logout();
      console.log("User data cleared from context and localStorage");
      router.push("/signin");
    } catch (error: any) {
      console.error("Logout failed:", error.response?.data || error.message);
      alert("Failed to log out. Please try again.");
      logout();
      router.push("/signin");
    }
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <header className="sticky top-0 z-[9999] w-full border-b bg-white shadow-sm">
      {/* Desktop Navbar */}
      <div className="mx-auto items-center justify-between px-4 md:px-6 hidden md:flex">
        <div className="flex items-center">
          <Link href="/" className="flex items-center gap-2">
            <Image
              className="w-[80px] h-auto"
              src={getLogo()}
              alt="Logo"
              width={80}
              height={32}
              style={{ objectFit: 'contain' }}
            />
          </Link>

        </div>

        <div>
          <nav className="flex items-center relative">
            {mainCategories.map((mainCat) => {
              const subCats = getSubCategories(mainCat.id);
              return (
                <div
                  key={mainCat.id}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(mainCat.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  {subCats.length > 0 ? (
                    <>
                      <Link
                        href={`/products/${mainCat.seo_url}`}
                        className="group text-[14px] font-medium transition-colors w-fit bg-white md:!px-2 !px-1 flex items-center gap-1"
                      >
                        {mainCat.name}
                        <ChevronDown
                          className={`transition-transform duration-200 !w-[14px] ${activeDropdown === mainCat.name ? "rotate-180" : ""
                            }`}
                        />
                      </Link>
                      {activeDropdown === mainCat.name && (
                        <div className="absolute left-0 top-full min-w-[160px] bg-gray-50 rounded-none z-50 shadow-md">
                          <ul className="flex flex-col">
                            {subCats.map((subCat) => (
                              <li key={subCat.id} className="px-4 py-2 hover:bg-gray-200">
                                <Link
                                  className="text-[14px] whitespace-nowrap"
                                  href={`/products/${subCat.seo_url}`}
                                >
                                  {subCat.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    </>
                  ) : (
                    <div>
                      <Link
                        href={`/products/${mainCat.seo_url}`}
                        className="text-[14px] font-medium transition-colors px-2"
                      >
                        {mainCat.name}
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center md:space-x-4 !space-x-[0px]">
          <div className="hidden md:block">
            <div
              className=""
              onClick={() => setShowSearchOverlay(true)}
            >
              <Search className="h-5 w-5 text-gray-500 mr-2" />
              {/* <input
                type="text"
                className="bg-transparent outline-none placeholder:text-[13px]"
                placeholder="Search Tamizh Tshirt"
              /> */}
            </div>
          </div>
          <Button variant="ghost" aria-label="Wishlist Icon" size="icon" className="h-8 w-8">
            <Link href="/my-wishlist" aria-label="Wishlist Icon">
              <Heart className="h-5 w-5" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" aria-label="Cart Icon" className="h-8 w-8 relative">
            <Link href="/cart" aria-label="Cart Icon" >
              {(cartinfoData?.cartcount ?? 0) > 0 && (
                <div className="absolute top-0 right-0 bg-red-500 rounded-full h-[15px] w-[15px] flex justify-center items-center">
                  <p className="text-[10px] text-white font-semibold">{cartinfoData?.cartcount}</p>
                </div>
              )}
              <ShoppingCart className="h-5 w-5" />
            </Link>
          </Button>
          {isLoggedIn ? (
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("user")}
              onMouseLeave={handleMouseLeave}
            >
              <button aria-label="Profile Icon" className="h-8 w-8 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-user-round-check-icon lucide-user-round-check h-5 w-5 text-primary"
                >
                  <path d="M2 21a8 8 0 0 1 13.292-6" />
                  <circle cx="10" cy="8" r="5" />
                  <path d="m16 19 2 2 4-4" />
                </svg>
              </button>
              {activeDropdown === "user" && (
                <div className="absolute right-0 top-6 mt-2 min-w-[160px] bg-white shadow-md z-50">
                  <ul className="flex flex-col">
                    <li className="px-4 py-2 text-[14px] font-medium capitalize">
                      {user?.username || "User"}
                    </li>
                    <li className="px-4 py-2 hover:bg-gray-200">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-user-round-pen-icon lucide-user-round-pen"
                        >
                          <path d="M2 21a8 8 0 0 1 10.821-7.487" />
                          <path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
                          <circle cx="10" cy="8" r="5" />
                        </svg>
                        <a href="/my-account" aria-label="Profile Icon" className="text-[14px] w-full block">
                          My Profile
                        </a>
                      </div>
                    </li>
                    <li className="px-4 py-2 hover:bg-gray-200">
                      <div className="flex items-center gap-2">
                        <svg aria-label="User Icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-log-out-icon lucide-log-out text-red-500"
                        >
                          <path d="m16 17 5-5-5-5" />
                          <path d="M21 12H9" />
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        </svg>
                        <button
                          onClick={handleLogout}
                          className="text-[14px] w-full text-left text-red-500"
                        >
                          Logout
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="h-8 w-8 flex items-center justify-center">
              <a href="/signin" aria-label="User Icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-user-round-icon lucide-user-round h-5 w-5"
                >
                  <circle cx="12" cy="8" r="5" />
                  <path d="M20 21a8 8 0 0 0-16 0" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Navbar */}
      <div className="container  mx-auto flex h-16 items-center justify-between px-4 md:px-6 md:hidden">
        <div className="w-2/12">
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button aria-label="Menu Button" variant="ghost" size="icon" className="h-8 w-8">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-fit p-0 !z-[9999]">
              <SheetHeader className="flex py-3 items-center justify-between border-b px-4">
                <div className="flex justify-between items-center w-full">
                  <SheetTitle>
                    <Link href="/" className="flex items-center gap-2">
                      <Image
                        className="w-[80px] h-auto"
                        src={getLogo()}
                        alt="Logo"
                        width={80}
                        height={32}
                        style={{ objectFit: 'contain' }}
                      />
                    </Link>
                  </SheetTitle>
                  <Button variant="ghost" size="icon" onClick={() => setIsSheetOpen(false)}>
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </div>
              </SheetHeader>
              <nav className="flex flex-col gap-2 p-4">
                {isLoggedIn ? (
                  <div>
                    <ul className="flex flex-col">
                      <li className="py-2">
                        <div className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-user-round-pen-icon lucide-user-round-pen"
                          >
                            <path d="M2 21a8 8 0 0 1 10.821-7.487" />
                            <path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
                            <circle cx="10" cy="8" r="5" />
                          </svg>
                          <a href="/my-account" className="text-[14px] w-full block">
                            My Profile
                          </a>
                        </div>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <button className="h-8 w-8 flex items-center justify-center">
                    <a href="/signin">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-user-round-icon lucide-user-round h-5 w-5"
                      >
                        <circle cx="12" cy="8" r="5" />
                        <path d="M20 21a8 8 0 0 0-16 0" />
                      </svg>
                    </a>
                  </button>
                )}
                {mainCategories.map((mainCat) => {
                  const subCats = getSubCategories(mainCat.id);
                  return (
                    <div key={mainCat.id}>
                      {subCats.length > 0 ? (
                        <details>
                          <summary className="cursor-pointer py-2 flex justify-between items-center text-[16px] font-medium">
                            {mainCat.name}
                            <ChevronDown className="h-4 w-4" />
                          </summary>
                          <ul className="ml-4">
                            {subCats.map((subCat) => (
                              <li key={subCat.id} className="py-1 text-gray-600 text-[14px]">
                                <Link
                                  href={`/products/${subCat.seo_url}`}
                                  onClick={() => setIsSheetOpen(false)}
                                >
                                  {subCat.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : (
                        <Button
                          variant="ghost"
                          asChild
                          className="w-full justify-start px-0 text-[16px] font-medium"
                        >
                          <Link
                            href={`/products/${mainCat.seo_url}`}
                            onClick={() => setIsSheetOpen(false)}
                          >
                            {mainCat.name}
                          </Link>
                        </Button>
                      )}
                    </div>
                  );
                })}
                {isLoggedIn && (
                  <div>
                    <ul className="flex flex-col">
                      <li className="py-2">
                        <div className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-log-out-icon lucide-log-out text-red-500"
                          >
                            <path d="m16 17 5-5-5-5" />
                            <path d="M21 12H9" />
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          </svg>
                          <button
                            onClick={handleLogout}
                            className="text-[14px] w-full text-left text-red-500"
                          >
                            Logout
                          </button>
                        </div>
                      </li>
                    </ul>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
        <Link href="/" className="flex-1 flex justify-center items-center w-6/12">
          <Image
            className="w-[80px] h-auto"
            src={getLogo()}
            alt="Logo"
            width={80}
            height={32}
            style={{ objectFit: 'contain' }}
          />
        </Link>
        <div className="flex justify-end items-center space-x-2 w-2/12">
          <Button aria-label="Search Button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowSearchOverlay(true)}>
            <Search className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 relative" aria-label="Cart Icon">
            <Link href="/cart" aria-label="Cart Icon">
              {(cartinfoData?.cartcount ?? 0) > 0 && (
                <div className="absolute top-0 right-0 bg-red-500 rounded-full h-[15px] w-[15px] flex justify-center items-center">
                  <p className="text-[10px] text-white font-semibold">{cartinfoData?.cartcount}</p>
                </div>
              )}
              <ShoppingCart className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
      <SearchOverlay isOpen={showSearchOverlay} onClose={() => setShowSearchOverlay(false)} />
    </header>
  );
};

export default Navbar;